import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import { T, PILL_SHADOW, FONT_MONO } from '../constants/tokens';

/**
 * The pill track and its buttons — the app's only view switcher shape, used by
 * the nav, the device tabs and the map's building and floor pickers.
 *
 * These are classes rather than inline objects because the design gives every
 * pill a hover state, and an inline style cannot express one. Four copies of
 * this markup had drifted before it was pulled in here.
 */
const useStyles = makeStyles({
  track: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
    width: 'max-content',
    maxWidth: '100%',
    background: T.pillTrack,
    borderRadius: 9999,
    padding: 6
  },
  pill: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    minWidth: 0,
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '-0.28px',
    padding: '8px 14px',
    border: 0,
    borderRadius: 6,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    background: 'transparent',
    color: T.body,
    transition: 'background 120ms ease, color 120ms ease',
    '&:hover:not($on):not(:disabled)': {
      background: T.railHover,
      color: T.ink
    },
    '&:focus-visible': {
      outline: '2px solid ' + T.ink,
      outlineOffset: 2
    },
    '&:disabled': {
      cursor: 'not-allowed',
      color: T.mutedSoft,
      opacity: 0.55
    }
  },
  on: {
    background: T.pillActive,
    color: T.ink,
    boxShadow: PILL_SHADOW
  },
  mono: {
    fontFamily: FONT_MONO,
    minWidth: 36,
    padding: '8px 0',
    justifyContent: 'center'
  },
  count: {
    fontFamily: FONT_MONO,
    fontSize: 12,
    fontWeight: 500,
    color: T.mutedSoft
  }
});

export function PillTrack({ children, style = {}, scroller = false }) {
  const classes = useStyles();
  return (
    <div
      className={classes.track}
      data-scroller={scroller ? '' : undefined}
      style={scroller ? { overflowX: 'auto', flexWrap: 'nowrap', ...style } : style}
    >
      {children}
    </div>
  );
}

PillTrack.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
  scroller: PropTypes.bool
};

export function Pill({ on = false, mono = false, count = null, disabled = false, onClick, title, children }) {
  const classes = useStyles();
  const className = [classes.pill, on ? classes.on : '', mono ? classes.mono : '']
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type='button'
      disabled={disabled}
      title={title}
      onClick={onClick}
      aria-current={on ? 'page' : undefined}
      className={className}
    >
      {children}
      {count != null && count !== '' &&
        <span className={classes.count}>{count}</span>}
    </button>
  );
}

Pill.propTypes = {
  on: PropTypes.bool,
  mono: PropTypes.bool,
  count: PropTypes.node,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node
};
